import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";

import Navbar from "../components/Navbar";
import Sidebar from "../components/Sidebar";
import Loader from "../components/Loader";
import ConfirmModal from "../components/ConfirmModal";
import Pagination from "../components/Pagination";

import authService from "../services/authService";
import playerService from "../services/playerService";
import transferService from "../services/transferService";

const ITEMS_PER_PAGE = 8;

const formatCoins = (value) => {
  return Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatDate = (value) => {
  if (!value) {
    return "N/A";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "N/A";
  }

  return date.toLocaleString("en-IN");
};

const Transfer = () => {
  const location = useLocation();

  const routePlayerId = location.state?.player?.id
    ? String(location.state.player.id)
    : "";

  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [user, setUser] = useState(
    authService.getCurrentUser()
  );

  const [players, setPlayers] = useState([]);
  const [transactions, setTransactions] = useState([]);

  const [selectedPlayerId, setSelectedPlayerId] =
    useState(routePlayerId);

  const [amount, setAmount] = useState("");

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [transferring, setTransferring] =
    useState(false);

  const [showConfirm, setShowConfirm] =
    useState(false);

  const [currentPage, setCurrentPage] =
    useState(1);

  const toggleSidebar = () => {
    setSidebarOpen((previous) => !previous);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  const fetchInitialData = useCallback(
    async ({ silent = false } = {}) => {
      try {
        if (silent) {
          setRefreshing(true);
        } else {
          setLoading(true);
        }

        const currentUser =
          authService.getCurrentUser();

        setUser(currentUser);

        const [playersResult, historyResult] =
          await Promise.allSettled([
            playerService.getPlayers(),
            transferService.getTransferHistory(),
          ]);

        if (playersResult.status === "fulfilled") {
          const response = playersResult.value;

          if (response?.success) {
            setPlayers(
              Array.isArray(response.players)
                ? response.players
                : []
            );
          } else {
            setPlayers([]);

            toast.error(
              response?.message ||
              "Unable to load Players."
            );
          }
        } else {
          setPlayers([]);

          toast.error(
            playersResult.reason?.response?.data
              ?.message ||
            "Unable to load Players."
          );
        }

        if (historyResult.status === "fulfilled") {
          const response = historyResult.value;

          const rows = Array.isArray(
            response?.transactions
          )
            ? response.transactions
            : Array.isArray(response?.history)
              ? response.history
              : Array.isArray(response?.data)
                ? response.data
                : [];

          setTransactions(rows);
        } else {
          setTransactions([]);

          console.error(
            "Transfer History Error:",
            historyResult.reason
          );
        }
      } catch (error) {
        console.error(
          "Load Transfer Data Error:",
          error
        );

        toast.error(
          error.response?.data?.message ||
          "Unable to load transfer data."
        );
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    []
  );

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  const selectedPlayer = useMemo(() => {
    return players.find(
      (player) =>
        String(player.id) ===
        String(selectedPlayerId)
    );
  }, [players, selectedPlayerId]);

  const isPlayerActive = (player) => {
    return (
      String(
        player?.status || "ACTIVE"
      ).toUpperCase() === "ACTIVE"
    );
  };

  const playerIds = useMemo(() => {
    return new Set(
      players
        .map((player) => Number(player.id))
        .filter((id) => Number.isInteger(id))
    );
  }, [players]);

  const playerUsernames = useMemo(() => {
    return new Set(
      players
        .map((player) =>
          String(
            player.username || ""
          ).toLowerCase()
        )
        .filter(Boolean)
    );
  }, [players]);

  const filteredTransactions = useMemo(() => {
    const loggedInUserId = Number(user?.id);

    const loggedInUsername = String(
      user?.username || ""
    ).toLowerCase();

    return transactions.filter((transaction) => {
      const transactionType = String(
        transaction.type || ""
      ).toUpperCase();

      if (
        transactionType &&
        transactionType !== "TRANSFER"
      ) {
        return false;
      }

      const senderId = Number(
        transaction.sender_id
      );

      const receiverId = Number(
        transaction.receiver_id
      );

      const senderUsername = String(
        transaction.sender_username ||
        transaction.sender ||
        ""
      ).toLowerCase();

      const receiverUsername = String(
        transaction.receiver_username ||
        transaction.receiver ||
        ""
      ).toLowerCase();

      const senderMatches =
        Number.isInteger(senderId) &&
          senderId > 0
          ? senderId === loggedInUserId
          : Boolean(loggedInUsername) &&
          senderUsername === loggedInUsername;

      const receiverMatches =
        Number.isInteger(receiverId) &&
          receiverId > 0
          ? playerIds.has(receiverId)
          : playerUsernames.has(
            receiverUsername
          );

      return senderMatches && receiverMatches;
    });
  }, [
    transactions,
    playerIds,
    playerUsernames,
    user?.id,
    user?.username,
  ]);

  const totalPages = Math.max(
    1,
    Math.ceil(
      filteredTransactions.length /
      ITEMS_PER_PAGE
    )
  );

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const currentTransactions = useMemo(() => {
    const startIndex =
      (currentPage - 1) * ITEMS_PER_PAGE;

    return filteredTransactions.slice(
      startIndex,
      startIndex + ITEMS_PER_PAGE
    );
  }, [filteredTransactions, currentPage]);

  const saveShopWallet = (wallet) => {
    const currentUser =
      authService.getCurrentUser() || user || {};

    const updatedUser = {
      ...currentUser,
      wallet: Number(wallet),
    };

    authService.setCurrentUser(updatedUser);
    setUser(updatedUser);
  };

  const handlePreTransferSubmit = (event) => {
    event.preventDefault();

    const transferAmount = Number(amount);

    if (!selectedPlayerId) {
      toast.error("Please select a Player.");
      return;
    }

    if (
      !Number.isFinite(transferAmount) ||
      transferAmount <= 0
    ) {
      toast.error(
        "Please enter a valid transfer amount."
      );
      return;
    }

    if (!selectedPlayer) {
      toast.error(
        "Selected Player was not found."
      );
      return;
    }

    if (!isPlayerActive(selectedPlayer)) {
      toast.error(
        "Coins cannot be transferred to a blocked Player."
      );
      return;
    }

    const shopWallet = Number(
      user?.wallet || 0
    );

    if (transferAmount > shopWallet) {
      toast.error(
        `Insufficient wallet balance. Available: 🪙 ${formatCoins(
          shopWallet
        )}`
      );
      return;
    }

    setShowConfirm(true);
  };

  const handleConfirmTransfer = async () => {
    const playerId = Number(selectedPlayerId);
    const transferAmount = Number(amount);

    if (
      !Number.isInteger(playerId) ||
      playerId <= 0 ||
      !Number.isFinite(transferAmount) ||
      transferAmount <= 0
    ) {
      toast.error(
        "Invalid Player or transfer amount."
      );

      setShowConfirm(false);
      return;
    }

    try {
      setTransferring(true);

      const response =
        await transferService.transferToPlayer({
          player_id: playerId,
          amount: transferAmount,
        });

      if (!response || response.success === false) {
        toast.error(
          response?.message ||
          "Unable to transfer coins."
        );
        return;
      }

      const balances = response.balances || {};

      const currentShopWallet = Number(
        user?.wallet || 0
      );

      const currentPlayerWallet = Number(
        selectedPlayer?.wallet || 0
      );

      const updatedShopWallet =
        balances.shop_wallet !== undefined
          ? Number(balances.shop_wallet)
          : currentShopWallet - transferAmount;

      const updatedPlayerWallet =
        balances.player_wallet !== undefined
          ? Number(balances.player_wallet)
          : currentPlayerWallet + transferAmount;

      saveShopWallet(updatedShopWallet);

      setPlayers((previous) =>
        previous.map((player) =>
          Number(player.id) === playerId
            ? {
              ...player,
              wallet: updatedPlayerWallet,
            }
            : player
        )
      );

      toast.success(
        response.message ||
        "Coins transferred to Player successfully."
      );

      setSelectedPlayerId("");
      setAmount("");
      setShowConfirm(false);
      setCurrentPage(1);

      await fetchInitialData({
        silent: true,
      });
    } catch (error) {
      console.error(
        "Transfer To Player Error:",
        error
      );

      toast.error(
        error.response?.data?.message ||
        "Unable to transfer coins to Player."
      );
    } finally {
      setTransferring(false);
    }
  };

  return (
    <div className="d-flex flex-column min-vh-100 bg-light">
      <Navbar toggleSidebar={toggleSidebar} />

      <div className="d-flex flex-grow-1 position-relative">
        <Sidebar
          isOpen={sidebarOpen}
          closeMobileSidebar={closeSidebar}
        />

        <main className="flex-grow-1 p-3 p-md-4 overflow-auto">
          <div className="container-fluid">
            <div className="d-flex flex-column flex-sm-row justify-content-between align-items-sm-center gap-3 mb-4 pb-3 border-bottom">
              <div>
                <h2 className="fw-bold text-dark mb-1">
                  Transfer Coins to Player
                </h2>

                <p className="text-secondary mb-0">
                  Transfer coins from your Shop wallet
                  to one of your registered Players.
                </p>
              </div>

              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={() =>
                  fetchInitialData({
                    silent: true,
                  })
                }
                disabled={
                  loading ||
                  refreshing ||
                  transferring
                }
              >
                {refreshing ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    Refreshing...
                  </>
                ) : (
                  <>
                    <i className="bi bi-arrow-clockwise me-2" />
                    Refresh
                  </>
                )}
              </button>
            </div>

            <div className="row g-4">
              <div className="col-12 col-xl-5">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-primary text-white p-3">
                    <h5 className="fw-bold mb-0">
                      <i className="bi bi-send me-2" />
                      Send Coins
                    </h5>
                  </div>

                  <div className="card-body p-4">
                    <div className="p-3 mb-4 rounded-3 bg-light border d-flex justify-content-between align-items-center">
                      <div>
                        <div className="small text-secondary fw-semibold">
                          SHOP WALLET BALANCE
                        </div>

                        <h4 className="fw-bold text-success mb-0">
                          🪙 {formatCoins(user?.wallet)}
                        </h4>
                      </div>

                      <i className="bi bi-wallet2 text-primary fs-1" />
                    </div>

                    {loading ? (
                      <Loader message="Loading Players..." />
                    ) : (
                      <form
                        onSubmit={
                          handlePreTransferSubmit
                        }
                      >
                        <div className="mb-3">
                          <label
                            htmlFor="playerId"
                            className="form-label fw-semibold"
                          >
                            Select Player *
                          </label>

                          <select
                            id="playerId"
                            className="form-select py-2"
                            value={selectedPlayerId}
                            onChange={(event) =>
                              setSelectedPlayerId(
                                event.target.value
                              )
                            }
                            disabled={
                              transferring ||
                              players.length === 0
                            }
                            required
                          >
                            <option value="">
                              {players.length === 0
                                ? "No Players available"
                                : "-- Choose Player --"}
                            </option>

                            {players.map((player) => (
                              <option
                                key={player.id}
                                value={player.id}
                                disabled={
                                  !isPlayerActive(
                                    player
                                  )
                                }
                              >
                                {player.full_name ||
                                  player.username}{" "}
                                (@
                                {player.username ||
                                  "unknown"}
                                ) — 🪙{" "}
                                {formatCoins(
                                  player.wallet
                                )}
                                {!isPlayerActive(
                                  player
                                )
                                  ? " — BLOCKED"
                                  : ""}
                              </option>
                            ))}
                          </select>
                        </div>

                        {selectedPlayer && (
                          <div className="alert alert-light border mb-3">
                            <div className="row g-3">
                              <div className="col-12 col-sm-6">
                                <div className="small text-secondary">
                                  Player
                                </div>

                                <div className="fw-semibold">
                                  {selectedPlayer.full_name ||
                                    "N/A"}
                                </div>

                                <div className="small text-primary">
                                  @
                                  {selectedPlayer.username ||
                                    "N/A"}
                                </div>
                              </div>

                              <div className="col-12 col-sm-6">
                                <div className="small text-secondary">
                                  Current Wallet
                                </div>

                                <div className="fw-bold text-success">
                                  🪙{" "}
                                  {formatCoins(
                                    selectedPlayer.wallet
                                  )}
                                </div>
                              </div>

                              <div className="col-12 col-sm-6">
                                <div className="small text-secondary">
                                  Mobile
                                </div>

                                <div className="fw-semibold">
                                  {selectedPlayer.mobile ||
                                    "N/A"}
                                </div>
                              </div>

                              <div className="col-12 col-sm-6">
                                <div className="small text-secondary">
                                  Status
                                </div>

                                <span
                                  className={`badge ${isPlayerActive(
                                    selectedPlayer
                                  )
                                      ? "bg-success"
                                      : "bg-danger"
                                    }`}
                                >
                                  {selectedPlayer.status ||
                                    "ACTIVE"}
                                </span>
                              </div>
                            </div>
                          </div>
                        )}

                        <div className="mb-4">
                          <label
                            htmlFor="transferAmount"
                            className="form-label fw-semibold"
                          >
                            Coin Amount *
                          </label>

                          <div className="input-group">
                            <span className="input-group-text bg-light fw-bold">
                              🪙
                            </span>

                            <input
                              id="transferAmount"
                              type="number"
                              min="0.01"
                              step="0.01"
                              className="form-control py-2"
                              placeholder="Enter transfer amount"
                              value={amount}
                              onChange={(event) =>
                                setAmount(
                                  event.target.value
                                )
                              }
                              disabled={transferring}
                              required
                            />
                          </div>

                          <div className="form-text">
                            Available balance: 🪙{" "}
                            {formatCoins(user?.wallet)}
                          </div>

                          {amount &&
                            Number(amount) >
                            Number(
                              user?.wallet || 0
                            ) && (
                              <div className="small text-danger fw-semibold mt-1">
                                Amount exceeds your
                                Shop wallet balance.
                              </div>
                            )}
                        </div>

                        <button
                          type="submit"
                          className="btn btn-primary w-100 py-3 fw-bold"
                          disabled={
                            transferring ||
                            !selectedPlayerId ||
                            !amount ||
                            Number(amount) <= 0 ||
                            Number(amount) >
                            Number(
                              user?.wallet || 0
                            )
                          }
                        >
                          <i className="bi bi-send-fill me-2" />
                          Transfer to Player
                        </button>
                      </form>
                    )}
                  </div>
                </div>
              </div>

              <div className="col-12 col-xl-7">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-white border-0 p-4 pb-2">
                    <h5 className="fw-bold mb-1">
                      Player Transfer History
                    </h5>

                    <p className="small text-secondary mb-0">
                      Transfers from this Shop to its
                      Players.
                    </p>
                  </div>

                  <div className="card-body p-0">
                    {loading ? (
                      <Loader message="Loading transfer history..." />
                    ) : currentTransactions.length ===
                      0 ? (
                      <div className="text-center py-5 px-3">
                        <i className="bi bi-receipt display-4 text-secondary" />

                        <h5 className="fw-bold mt-3">
                          No Player Transfers Found
                        </h5>

                        <p className="text-secondary mb-0">
                          Completed transfers will appear
                          here.
                        </p>
                      </div>
                    ) : (
                      <div className="table-responsive">
                        <table className="table table-hover align-middle mb-0">
                          <thead className="table-light">
                            <tr>
                              <th className="ps-4">
                                ID
                              </th>
                              <th>Player</th>
                              <th>Amount</th>
                              <th>Type</th>
                              <th>Date</th>
                            </tr>
                          </thead>

                          <tbody>
                            {currentTransactions.map(
                              (transaction) => {
                                const receiverPlayer =
                                  players.find(
                                    (player) =>
                                      Number(
                                        player.id
                                      ) ===
                                      Number(
                                        transaction.receiver_id
                                      )
                                  );

                                return (
                                  <tr
                                    key={
                                      transaction.id ||
                                      `${transaction.receiver_id}-${transaction.created_at}`
                                    }
                                  >
                                    <td className="ps-4 fw-bold text-secondary">
                                      #
                                      {transaction.id ||
                                        "N/A"}
                                    </td>

                                    <td>
                                      <div className="fw-semibold">
                                        {receiverPlayer?.full_name ||
                                          transaction.receiver_username ||
                                          transaction.receiver ||
                                          "Player"}
                                      </div>

                                      <div className="small text-primary">
                                        @
                                        {receiverPlayer?.username ||
                                          transaction.receiver_username ||
                                          "N/A"}
                                      </div>
                                    </td>

                                    <td className="fw-bold text-danger">
                                      -🪙{" "}
                                      {formatCoins(
                                        transaction.amount
                                      )}
                                    </td>

                                    <td>
                                      <span className="badge bg-primary-subtle text-primary border border-primary-subtle">
                                        {transaction.type ||
                                          "TRANSFER"}
                                      </span>
                                    </td>

                                    <td className="small text-secondary">
                                      {formatDate(
                                        transaction.created_at
                                      )}
                                    </td>
                                  </tr>
                                );
                              }
                            )}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  {!loading &&
                    filteredTransactions.length >
                    0 && (
                      <div className="card-footer bg-white border-top p-3">
                        <Pagination
                          currentPage={currentPage}
                          totalPages={totalPages}
                          onPageChange={
                            setCurrentPage
                          }
                        />
                      </div>
                    )}
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>

      <ConfirmModal
        show={showConfirm}
        title="Confirm Player Transfer"
        message={`Transfer 🪙 ${formatCoins(
          amount
        )} coins to ${selectedPlayer?.full_name ||
          "selected Player"
          } (@${selectedPlayer?.username || "N/A"
          })?`}
        onConfirm={handleConfirmTransfer}
        onClose={() => {
          if (!transferring) {
            setShowConfirm(false);
          }
        }}
        loading={transferring}
        variant="primary"
        confirmText="Yes, Send Coins"
      />
    </div>
  );
};

export default Transfer;